import React, { useState, useEffect } from "react";
import PageHeadline from "../../components/shared/PageHeadline";
import { useTranslation } from "react-i18next";
import { useFetchEventHistory, useUserProfile } from "../../hooks/useApi";
import { isAuthenticated, removeToken } from "../../lib/api";
import Profile, { ProfileData } from "./Profile";
import { useNavigate } from "react-router-dom";

export default function Index(): React.ReactElement | null {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isClient, setIsClient] = useState(false);
    
    // Check authentication guard on mount
    useEffect(() => {
        setIsClient(true);
        if (!isAuthenticated()) {
            navigate("/login");
        }
    }, [navigate]);

    // Fetch profile and event history
    const { data: userProfile, isLoading: isProfileLoading, error: profileError } = useUserProfile();
    const { data: eventHistoryData, isLoading: isEventHistoryLoading, error: eventHistoryError } = useFetchEventHistory();

    const handleLogout = () => {
        removeToken();
        navigate("/login");
    };

    // Transform API data structure to presentational ProfileData structure
    const transformProfileData = (apiData: any): ProfileData | undefined => {
        if (!apiData) return undefined;
        const profile = apiData.data || apiData;
        if (!profile) return undefined;
        
        return {
            uusrId: profile.uusrId || 0,
            uusrName: profile.uusrName || "",
            uusrEmail: profile.uusrEmail || "",
            uusrPhoneNumber: profile.uusrPhoneNumber || profile.uusrPhone || "",
            uusrGender: profile.uusrGender || 0,
            uusrBloodType: profile.uusrBloodType || "",
            uusrNationality: profile.uusrNationality || "",
            uusrIdentityType: profile.uusrIdentityType || 1,
            uusrIdentityNumber: profile.uusrIdentityNumber || profile.uusrIdentityNo || profile.uusrIdCard || "",
            uusrDateOfBirth: profile.uusrDateOfBirth || profile.uusrBirth || profile.uusrBirthDate || "",
            uusrOccupation: profile.uusrOccupation || "",
            uusrCommunity: profile.uusrCommunity || "",
        };
    };

    const profileData = transformProfileData(userProfile);

    if (!isAuthenticated()) {
        return null;
    }

    if (!isClient) {
        return (
            <div className="text-[#164481] md:px-0 px-4 bg-white min-h-screen">
                <div className="relative h-[240px] md:h-[300px] bg-brand-blue flex justify-center items-center mt-[86px] md:mt-[109px] 2xl:mt-[136px]">
                    <PageHeadline title={t("profile.title", "Profile")} />
                </div>
                <div className="relative top-12 md:top-40 lg:top-60 bg-white 2xl:px-0 md:px-14 px-0 py-5 pb-96">
                    <div className="bg-white min-h-screen container mx-auto px-4 w-full font-poppins text-[#164481]">
                        <div className="flex items-center justify-center h-64">
                            <div className="animate-pulse text-lg">Loading...</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="text-[#164481] md:px-0 px-4 bg-white 2xl:pb-96 lg:pb-96 md:pb-80 pb-40">
            {/* Page Header Banner */}
            <div className="relative h-[240px] md:h-[300px] bg-brand-blue flex justify-center items-center mt-[86px] md:mt-[109px] 2xl:mt-[136px]">
                <PageHeadline title={t("profile.title", "Profile")} />
            </div>

            {/* Main Content Area */}
            <div className="relative top-12 md:top-40 bg-white 2xl:px-0 md:px-14 px-0 py-5 pb-20 lg:pb-40">
                <div className="bg-white container mx-auto px-4 w-full font-poppins text-[#164481]">
                    <Profile
                        data={profileData}
                        eventHistory={eventHistoryData}
                        isLoading={isProfileLoading}
                        error={profileError instanceof Error ? profileError : null}
                        isEventHistoryLoading={isEventHistoryLoading}
                        eventHistoryError={eventHistoryError instanceof Error ? eventHistoryError : null}
                        onLogout={handleLogout}
                    />
                </div>
            </div>
        </div>
    );
}
